import { FC, memo, useCallback, useEffect } from 'react';
import { AnimatePresence } from 'motion/react';
import { useAudioContext, useAudioPlayback } from 'src/context/AudioContext';
import { useKeyboardShortcut } from 'src/hooks';
import { defineMessages, useIntl } from 'react-intl';
import * as S from './styles';

const msgs = defineMessages({
  audio: { id: 'learning_session_learnables_card_audio' },
});

type WrappedAudioPlayerProps = {
  src: string;
  autoPlay?: boolean;
  playOnKeyboardShortcut?: boolean;
  larger?: boolean;
  className?: string;
  ariaText?: string;
  onClick?: () => void;
  onEnded?: () => void;
};

const WrappedAudioPlayer: FC<WrappedAudioPlayerProps> = ({
  src,
  autoPlay = false,
  playOnKeyboardShortcut = false,
  larger = false,
  onClick,
  onEnded,
  ...restProps
}) => {
  const { playing, play, stopAll } = useAudioPlayback(src);
  const { audioObject } = useAudioContext();
  const intl = useIntl();

  const togglePlay = useCallback(async () => {
    if (playing) {
      stopAll();
    } else {
      await play();
    }
    if (onClick) onClick();
  }, [onClick, play, playing, stopAll]);

  useEffect(() => {
    if (autoPlay) void play();
  }, [autoPlay, play]);

  useEffect(() => {
    const audio = audioObject[src];
    if (!audio || !onEnded) return;

    audio.addEventListener('ended', onEnded);
    return () => audio.removeEventListener('ended', onEnded);
  }, [audioObject, onEnded, src]);

  useKeyboardShortcut('r', playOnKeyboardShortcut ? play : undefined);

  const props = { ...restProps, onClick: togglePlay, playing };

  return larger ? (
    <LargeAudioPlayer ariaText={intl.formatMessage(msgs.audio)} {...props} />
  ) : (
    <AudioPlayer ariaText={intl.formatMessage(msgs.audio)} {...props} />
  );
};

type AudioPlayerProps = {
  onClick: () => void;
  playing: boolean;
  className?: string;
  ariaText?: string;
};

export const LargeAudioPlayer: FC<AudioPlayerProps> = memo(function LargeAudioPlayer({
  onClick,
  playing,
  className,
  ariaText,
  ...props
}) {
  return (
    <S.ButtonBackground {...props}>
      <S.PulseBackground>
        <S.Button
          aria-label={ariaText}
          title={ariaText}
          className={className}
          onClick={onClick}
          data-testid="audioPlayer"
          $larger
        >
          <AnimatePresence>
            {playing && (
              <S.PulseLarger
                data-testid="pulse"
                initial={{
                  scale: 0,
                  opacity: 0,
                }}
                exit={{
                  scale: 0,
                  opacity: 0,
                }}
                animate={{
                  scale: [0.8, 1, 0.8],
                  opacity: [0.05, 1, 0.05],
                  transition: {
                    duration: 0.8,
                    repeat: Infinity,
                    repeatType: 'loop' as const,
                    ease: 'easeInOut',
                    repeatDelay: 0.15,
                  },
                }}
              />
            )}
          </AnimatePresence>
          <S.SpeakerIcon $larger />
        </S.Button>
      </S.PulseBackground>
    </S.ButtonBackground>
  );
});

export const AudioPlayer: FC<AudioPlayerProps> = memo(function AudioPlayer({
  onClick,
  playing,
  ariaText,
  ...props
}) {
  return (
    <S.Button
      aria-label={ariaText}
      title={ariaText}
      onClick={onClick}
      data-testid="audioPlayer"
      aria-pressed={playing}
      {...props}
    >
      <AnimatePresence>
        {playing && (
          <S.Pulse
            data-testid="pulse"
            initial={{
              scale: 0,
              opacity: 0,
            }}
            exit={{
              scale: 0,
              opacity: 0,
            }}
            animate={{
              scale: [0.8, 1, 0.8],
              opacity: [0.05, 1, 0.05],
              transition: {
                duration: 0.8,
                repeat: Infinity,
                repeatType: 'loop' as const,
                ease: 'easeInOut',
                repeatDelay: 0.15,
              },
            }}
          />
        )}
      </AnimatePresence>
      <S.SpeakerIcon />
    </S.Button>
  );
});

export default WrappedAudioPlayer;
